import { qeddyText } from "./qeddyTranslations";
import React from "react";
import { createBackgroundRefresh } from "../system/backgroundRefresh";
import { QeddyDrawer, type QeddyDrawerFeedbackReason, type QeddyDrawerMessage, type QeddyDrawerMessageKind, type QeddyDrawerSource } from "./QeddyDrawer";
import { QeddyLauncher, type QeddyLauncherProps, type QeddyUiState } from "./QeddyLauncher";
import { QeddyProvider } from "./QeddyProvider";
type QeddyConversationPolicy = {
    idleTimeoutMinutes: number;
    maxPromptCharacters: number;
};
type QeddyStatus = {
    state: QeddyUiState;
    reason?: string | null;
    tooltip?: string | null;
    conversationPolicy?: QeddyConversationPolicy;
};
type QeddyConversationAnchor = {
    standaloneQuestion?: string | null;
    sourceIds?: string[] | null;
};
type QeddyAskResponse = {
    state: QeddyUiState;
    answer?: string | null;
    sources?: QeddyDrawerSource[];
    errorClass?: string | null;
    resultType: "answer" | "clarifyingQuestion" | "suggestions" | "notAnswerable" | "operationalError" | "contextPrepared";
    clarifyingQuestion?: string | null;
    anchor?: QeddyConversationAnchor | null;
    interactionId?: string | null;
    conversationContext?: QeddyConversationMemory | null;
};
type QeddyAskHistoryMessage = {
    role: "user" | "assistant";
    content: string;
};
type QeddyConversationMemory = {
    summary?: string | null;
    history: QeddyAskHistoryMessage[];
    compactionCount: number;
};
function conversationCharacterCount(history: QeddyAskHistoryMessage[], summary?: string | null) {
    const messages = summary ? [{ role: "summary", content: summary }, ...history] : history;
    // Zwei Zeichen für den Zeilenumbruch decken Windows und Linux konservativ ab.
    return messages.reduce((count, message) => count + message.role.length + LINE_BREAK_CHARACTERS + message.content.trim().length + LINE_BREAK_CHARACTERS, 0);
}
const qeddyDefaultIdleTimeoutMinutes = 120;
const LINE_BREAK_CHARACTERS = 2;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;
const qeddyDisabledStatusRefreshMs = 30000;
function newId() {
    try {
        return crypto.randomUUID();
    }
    catch {
        return `${Date.now()}-${Math.random()}`;
    }
}
function getCurrentAppRoute() {
    const hashRoute = window.location.hash.startsWith("#/")
        ? window.location.hash.slice(1)
        : "";
    return hashRoute || window.location.pathname || "/";
}
function buildUiContext(currentRoute: string) {
    const pageKey = currentRoute.split(/[?#]/, 1)[0] || "/";
    return { pageKey, tabKey: null, viewMode: null };
}
function buildHistory(messages: QeddyDrawerMessage[], currentQuestion?: string): QeddyAskHistoryMessage[] {
    let lastResetIndex = -1;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        if (messages[index]?.kind !== "conversationReset")
            continue;
        lastResetIndex = index;
        break;
    }
    const activeMessages = messages.slice(lastResetIndex + 1);
    const withoutPendingQuestion = currentQuestion && activeMessages.at(-1)?.role === "user" && activeMessages.at(-1)?.content === currentQuestion
        ? activeMessages.slice(0, -1)
        : activeMessages;
    return withoutPendingQuestion
        .filter((message): message is QeddyDrawerMessage & {
        role: "user" | "assistant";
    } => message.role === "user" || message.role === "assistant")
        .map((message) => ({ role: message.role, content: message.content.trim() }))
        .filter((message) => message.content.length > 0);
}
function resultKind(resultType: QeddyAskResponse["resultType"]): QeddyDrawerMessageKind {
    if (resultType === "clarifyingQuestion")
        return "clarifyingQuestion";
    if (resultType === "suggestions")
        return "suggestions";
    if (resultType === "notAnswerable")
        return "notAnswerable";
    return "answer";
}
export type QeddyAssistantProps = {
    api: QeddyAssistantApi;
    endpoint: string;
    enabled: boolean;
    scopeKey: string;
    language: string;
    productName: string;
    translate?: (key: string) => string;
    errorMessage?: (error: unknown) => string;
    onOpen?: () => void;
    onVisibilityChange?: (visible: boolean) => void;
    children?: React.ReactNode;
};
export function QeddyAssistant(props: QeddyAssistantProps) {
    return <QeddyAssistantSession key={`${props.scopeKey}:${props.language}:${props.enabled}`} {...props} />;
}

function QeddyAssistantSession({ api, endpoint, enabled, scopeKey, language, productName, translate, errorMessage, onOpen, onVisibilityChange, children }: QeddyAssistantProps) {
    const text = React.useCallback((key: string) => translate?.(key) ?? qeddyText(language, key, productName), [translate, language, productName]);
    const [status, setStatus] = React.useState<QeddyStatus>({ state: "hidden" });
    const [open, setOpen] = React.useState(false);
    const [messages, setMessages] = React.useState<QeddyDrawerMessage[]>([]);
    const [anchor, setAnchor] = React.useState<QeddyConversationAnchor | null>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [compacting, setCompacting] = React.useState(false);
    const modelContextRef = React.useRef<QeddyConversationMemory | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [retryQuestion, setRetryQuestion] = React.useState<string | null>(null);
    const conversationIdRef = React.useRef(newId());
    const turnIndexRef = React.useRef(0);
    const lastActivityAtRef = React.useRef<number | null>(null);
    const conversationGenerationRef = React.useRef(0);
    const submittingRef = React.useRef(false);
    const userContextKey = `${scopeKey}:${language}`;
    const activeUserContextKeyRef = React.useRef(userContextKey);
    const statusRequestRef = React.useRef(0);
    const unavailableText = React.useCallback(() => text("Qeddy unavailable"), [text]);
    const refreshStatus = React.useCallback(() => {
        const requestedUserContextKey = userContextKey;
        if (!enabled) return Promise.resolve();
        const requestId = ++statusRequestRef.current;
        const route = getCurrentAppRoute();
        return api.get<QeddyStatus>(`${endpoint}/status?language=${encodeURIComponent(language)}&currentRoute=${encodeURIComponent(route)}`, { suppressGlobalErrorToast: true })
            .then(data => {
                if (requestId === statusRequestRef.current && activeUserContextKeyRef.current === requestedUserContextKey) setStatus(data);
            }).catch(() => {
                if (requestId === statusRequestRef.current && activeUserContextKeyRef.current === requestedUserContextKey) setStatus({ state: "disabled", reason: "statusUnavailable" });
            });
    }, [api, endpoint, enabled, language, userContextKey]);
    React.useEffect(() => () => { conversationGenerationRef.current += 1; statusRequestRef.current += 1; submittingRef.current = false; }, []);
    React.useEffect(() => {
        void refreshStatus();
    }, [refreshStatus]);
    React.useEffect(() => {
        onVisibilityChange?.(status.state !== "hidden");
    }, [onVisibilityChange, status.state]);
    React.useEffect(() => {
        if (status.state !== "disabled")
            return undefined;
        const refresh = createBackgroundRefresh(() => void refreshStatus());
        const timer = window.setInterval(() => refresh.request(), qeddyDisabledStatusRefreshMs);
        return () => { window.clearInterval(timer); refresh.dispose(); };
    }, [refreshStatus, status.state]);
    React.useEffect(() => {
        const refresh = createBackgroundRefresh(() => void refreshStatus());
        const refreshOnFocus = () => {
            if (status.state !== "ready")
                refresh.request();
        };
        window.addEventListener("focus", refreshOnFocus);
        return () => { window.removeEventListener("focus", refreshOnFocus); refresh.dispose(); };
    }, [refreshStatus, status.state]);
    const hasConversationExpired = React.useCallback((now: number) => {
        if (lastActivityAtRef.current === null)
            return false;
        const idleMinutes = status.conversationPolicy?.idleTimeoutMinutes ?? qeddyDefaultIdleTimeoutMinutes;
        return now - lastActivityAtRef.current >= idleMinutes * SECONDS_PER_MINUTE * MS_PER_SECOND;
    }, [status.conversationPolicy?.idleTimeoutMinutes]);
    const startNewConversation = React.useCallback(() => {
        conversationGenerationRef.current += 1;
        submittingRef.current = false;
        setMessages([]);
        modelContextRef.current = null;
        setCompacting(false);
        setAnchor(null);
        setSubmitting(false);
        setError(null);
        setRetryQuestion(null);
        conversationIdRef.current = newId();
        turnIndexRef.current = 0;
        lastActivityAtRef.current = null;
    }, []);
    const ask = React.useCallback(async (question: string, appendUserMessage = true) => {
        if (submittingRef.current)
            return;
        const now = Date.now();
        const expired = hasConversationExpired(now);
        const resetMessage: QeddyDrawerMessage | null = expired && messages.length > 0
            ? {
                id: newId(),
                role: "system",
                kind: "conversationReset",
                content: text("Qeddy conversation expired")
            }
            : null;
        const activeMessages = resetMessage ? [...messages, resetMessage] : messages;
        const userMessage: QeddyDrawerMessage = { id: newId(), role: "user", kind: "user", content: question };
        const nextMessages = appendUserMessage ? [...activeMessages, userMessage] : activeMessages;
        const activeAnchor = expired ? null : anchor;
        if (expired) {
            conversationGenerationRef.current += 1;
            conversationIdRef.current = newId();
            turnIndexRef.current = 0;
            setAnchor(null);
            modelContextRef.current = null;
        }
        const requestGeneration = conversationGenerationRef.current;
        const requestUserContextKey = userContextKey;
        setMessages(nextMessages);
        lastActivityAtRef.current = now;
        turnIndexRef.current += 1;
        submittingRef.current = true;
        setSubmitting(true);
        setError(null);
        setRetryQuestion(null);
        const currentRoute = getCurrentAppRoute();
        try {
            const request = {
                question,
                language,
                currentRoute,
                uiContext: buildUiContext(currentRoute),
                history: modelContextRef.current?.history ?? buildHistory(activeMessages, appendUserMessage ? undefined : question),
                conversationSummary: modelContextRef.current?.summary ?? null,
                anchor: activeAnchor,
                conversationId: conversationIdRef.current,
                turnIndex: turnIndexRef.current
            };
            const isCurrentRequest = () => conversationGenerationRef.current === requestGeneration &&
                activeUserContextKeyRef.current === requestUserContextKey;
            const applyContext = (context: QeddyConversationMemory) => {
                modelContextRef.current = { ...context, compactionCount: 0 };
                if (context.compactionCount > 0) {
                    setMessages((current) => [...current, {
                            id: newId(), role: "system", kind: "conversationCompacted",
                            content: text("Qeddy conversation compacted")
                        }]);
                }
            };
            let response: QeddyAskResponse | undefined;
            const budget = status.conversationPolicy?.maxPromptCharacters;
            if (budget !== undefined && conversationCharacterCount(request.history, request.conversationSummary) >= budget) {
                setCompacting(true);
                const prepared = await api.post<QeddyAskResponse>(`${endpoint}/compact`, request, { suppressGlobalErrorToast: true });
                if (!isCurrentRequest())
                    return;
                setCompacting(false);
                if (prepared.state === "ready" && prepared.resultType === "contextPrepared" && prepared.conversationContext) {
                    applyContext(prepared.conversationContext);
                    request.history = prepared.conversationContext.history;
                    request.conversationSummary = prepared.conversationContext.summary ?? null;
                }
                else {
                    response = prepared;
                }
            }
            response ??= await api.post<QeddyAskResponse>(`${endpoint}/ask`, request, { suppressGlobalErrorToast: true });
            if (conversationGenerationRef.current !== requestGeneration ||
                activeUserContextKeyRef.current !== requestUserContextKey)
                return;
            if (response.conversationContext)
                applyContext(response.conversationContext);
            if (response.state !== "ready" || response.resultType === "operationalError" || response.resultType === "contextPrepared") {
                const reason = response.errorClass || "providerUnavailable";
                if (response.state === "disabled") {
                    setStatus((current) => ({ ...current, state: "disabled", reason, tooltip: unavailableText() }));
                }
                else {
                    void refreshStatus();
                }
                setRetryQuestion(question);
                setError(unavailableText());
                return;
            }
            setStatus(current => ({ ...current, state: "ready", reason: null, tooltip: null }));
            const assistantContent = response.answer || response.clarifyingQuestion;
            if (assistantContent) {
                const context = modelContextRef.current ?? {
                    summary: request.conversationSummary, history: request.history, compactionCount: 0
                };
                modelContextRef.current = {
                    ...context,
                    history: [...context.history, { role: "user", content: question },
                        { role: "assistant", content: assistantContent }]
                };
                setMessages((current) => [
                    ...current,
                    {
                        id: newId(),
                        role: "assistant",
                        kind: resultKind(response.resultType),
                        content: assistantContent,
                        sources: response.sources ?? [],
                        interactionId: response.interactionId
                    }
                ]);
            }
            setAnchor(response.anchor ?? activeAnchor);
            lastActivityAtRef.current = Date.now();
        }
        catch (caught) {
            if (conversationGenerationRef.current !== requestGeneration ||
                activeUserContextKeyRef.current !== requestUserContextKey)
                return;
            const message = errorMessage?.(caught) || text("Qeddy unavailable");
            setRetryQuestion(question);
            setError(message);
        }
        finally {
            if (conversationGenerationRef.current === requestGeneration &&
                activeUserContextKeyRef.current === requestUserContextKey) {
                submittingRef.current = false;
                setSubmitting(false);
                setCompacting(false);
            }
        }
    }, [anchor, api, endpoint, errorMessage, hasConversationExpired, language, messages, refreshStatus, status.conversationPolicy, text, unavailableText, userContextKey]);
    const submitFeedback = React.useCallback(async (messageId: string, interactionId: string, helpful: boolean, reason?: QeddyDrawerFeedbackReason) => {
        const generation = conversationGenerationRef.current;
        try {
            await api.post(`${endpoint}/interactions/${encodeURIComponent(interactionId)}/feedback`, {
                helpful,
                reason: reason ?? null
            }, { suppressGlobalErrorToast: true });
            if (generation !== conversationGenerationRef.current) return;
            setMessages((current) => current.map((message) => message.id === messageId
                ? {
                    ...message,
                    feedback: helpful ? "helpful" : "notHelpful",
                    feedbackReason: reason ?? null
                }
                : message));
        }
        catch {
            if (generation === conversationGenerationRef.current) setError(text("Qeddy feedback failed"));
        }
    }, [api, endpoint, text]);
    const openAssistant = React.useCallback(() => {
        onOpen?.();
        const now = Date.now();
        if (hasConversationExpired(now) && messages.length > 0) {
            conversationGenerationRef.current += 1;
            setMessages((current) => [
                ...current,
                { id: newId(), role: "system", kind: "conversationReset", content: text("Qeddy conversation expired") }
            ]);
            setAnchor(null);
            modelContextRef.current = null;
            conversationIdRef.current = newId();
            turnIndexRef.current = 0;
            lastActivityAtRef.current = null;
        }
        setOpen(true);
        void refreshStatus();
    }, [onOpen, hasConversationExpired, messages.length, text, refreshStatus]);
    // Frage von außen (Schnellsuche): Ablauf und Verfall des Gesprächs regelt `ask` selbst.
    const askFromOutside = React.useCallback((question: string) => {
        onOpen?.();
        setOpen(true);
        void refreshStatus();
        void ask(question);
    }, [ask, onOpen, refreshStatus]);
    const launcher: QeddyLauncherProps = {
        state: status.state,
        labels: { open: text("Open Qeddy"), unavailable: text("Qeddy unavailable") },
        disabledTooltip: status.tooltip || unavailableText(),
        onOpen: openAssistant
    };
    return (<QeddyProvider value={{ refreshStatus, launcher, ask: askFromOutside }}>
      {children ?? <QeddyLauncher {...launcher}/>}
      <QeddyDrawer open={open} labels={{
            title: text("Qeddy"),
            intro: text("Qeddy intro"),
            inputPlaceholder: text("Qeddy input placeholder"),
            send: text("Ask"),
            empty: text("Qeddy empty"),
            thinking: compacting ? text("Qeddy compacting") : text("Qeddy thinking"),
            error: text("Qeddy unavailable"),
            newConversation: text("Qeddy new conversation"),
            sources: text("Qeddy related pages"),
            openPage: text("Qeddy open page"),
            helpful: text("Qeddy helpful"),
            notHelpful: text("Qeddy not helpful"),
            feedbackThanks: text("Qeddy feedback thanks"),
            feedbackReasons: {
                notAnswered: text("Qeddy feedback not answered"),
                unclear: text("Qeddy feedback unclear"),
                outdated: text("Qeddy feedback outdated"),
                stepsIncorrect: text("Qeddy feedback steps incorrect")
            },
            suggestionsTitle: text("Qeddy suggestions"),
            retry: text("Retry")
        }} messages={messages} submitting={submitting} error={error} onAsk={ask} onNewConversation={startNewConversation} onFeedback={submitFeedback}
        onRetry={retryQuestion ? () => void ask(retryQuestion, false) : undefined} onClose={() => setOpen(false)}/>
    </QeddyProvider>);
}

export type QeddyAssistantApi = { get<T>(path: string, options?: unknown): Promise<T>; post<T>(path: string, body: unknown, options?: unknown): Promise<T> };
